import {
  type MediaType,
  TranscribeUploadedFileDocument,
  type TranscribeUploadedFileMutation,
  type TranscribeUploadedFileMutationVariables,
} from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { FiChevronRight, FiX } from "react-icons/fi";
import { MediaLinkSection } from "../MediaLinkSection";
import { getLinkableTypes, getMediaConfig, hasAudioText, hasTranscription } from "../config";
import { TagsSection } from "../forms";
import { MediaPreview } from "../previews";
import type { FileWithMetadata } from "./MediaUploadModal";

interface UploadPreviewPanelProps {
  /** Files with metadata */
  files: FileWithMetadata[];
  /** Media type being uploaded */
  mediaType: MediaType;
  /** Callback to remove a file */
  onRemoveFile: (index: number) => void;
  /** Callback to update file metadata */
  onUpdateMetadata: (index: number, updates: Partial<FileWithMetadata>) => void;
}

/**
 * UploadPreviewPanel - Right panel for upload modal
 *
 * Shows file previews and per-file metadata editing (audioText for audio/video).
 */
export function UploadPreviewPanel({
  files,
  mediaType,
  onRemoveFile,
  onUpdateMetadata,
}: UploadPreviewPanelProps) {
  const config = getMediaConfig(mediaType);
  const showAudioText = hasAudioText(mediaType);
  const showTranscription = hasTranscription(mediaType);
  const linkableTypes = getLinkableTypes(mediaType);
  const [expandedLinks, setExpandedLinks] = useState<Record<number, boolean>>({});
  const [transcribingFiles, setTranscribingFiles] = useState<Record<number, boolean>>({});
  const [transcriptionErrors, setTranscriptionErrors] = useState<Record<number, string | null>>({});

  // Transcription mutation
  const [transcribeFile] = useMutation<
    TranscribeUploadedFileMutation,
    TranscribeUploadedFileMutationVariables
  >(TranscribeUploadedFileDocument);

  const toggleLinks = (index: number) => {
    setExpandedLinks((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleLinkChange = (fileIndex: number, linkType: MediaType, ids: string[]) => {
    const file = files[fileIndex];
    if (!file) return;
    onUpdateMetadata(fileIndex, {
      links: {
        ...file.links,
        [linkType]: ids,
      },
    });
  };

  const handleTranscribe = async (fileIndex: number) => {
    const fileData = files[fileIndex];
    if (!fileData) return;

    setTranscribingFiles((prev) => ({ ...prev, [fileIndex]: true }));
    setTranscriptionErrors((prev) => ({ ...prev, [fileIndex]: null }));

    try {
      const result = await transcribeFile({
        variables: {
          file: fileData.file,
        },
      });

      const transcriptionResult = result.data?.transcribeUploadedFile;

      if (transcriptionResult?.success && transcriptionResult.text) {
        // Update audioText with transcription
        onUpdateMetadata(fileIndex, { audioText: transcriptionResult.text });
        toast.success(`Transcription complete! (${transcriptionResult.duration?.toFixed(1)}s)`);
      } else {
        const errorMsg = transcriptionResult?.error || "Transcription failed";
        setTranscriptionErrors((prev) => ({ ...prev, [fileIndex]: errorMsg }));
        toast.error(errorMsg);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Transcription failed";
      setTranscriptionErrors((prev) => ({ ...prev, [fileIndex]: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setTranscribingFiles((prev) => ({ ...prev, [fileIndex]: false }));
    }
  };

  if (files.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-gray-500">Upload {config.labelPlural.toLowerCase()} to preview</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Preview</h3>

      <div className="space-y-4">
        {files.map((fileData, index) => (
          <div
            key={`${fileData.file.name}-${index}`}
            className="rounded-lg border border-gray-200 p-4"
          >
            {/* File header with preview and remove button */}
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-600">
                {index + 1}
              </span>

              <div className="min-w-0 flex-1">
                <MediaPreview
                  url={fileData.preview}
                  filename={fileData.file.name}
                  type={mediaType}
                  variant="inline"
                />
              </div>

              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                aria-label={`Remove ${fileData.file.name}`}
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>

            {/* Per-file tags */}
            <div className="mt-3">
              <TagsSection
                value={fileData.tags}
                onChange={(newTags) => onUpdateMetadata(index, { tags: newTags })}
                required
                label="Tags"
                helpText="Add tags for this file"
              />
            </div>

            {/* Audio text input for audio/video */}
            {showAudioText && (
              <div className="mt-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Description / Transcription
                </label>
                <textarea
                  value={fileData.audioText}
                  onChange={(e) => onUpdateMetadata(index, { audioText: e.target.value })}
                  placeholder="Enter description or transcription..."
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />

                {/* Transcription button */}
                {showTranscription && (
                  <div className="mt-2 space-y-1">
                    <button
                      type="button"
                      onClick={() => handleTranscribe(index)}
                      disabled={transcribingFiles[index]}
                      className="rounded bg-blue-600 px-3 py-1.5 text-xs text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {transcribingFiles[index] ? "Transcribing..." : "To text"}
                    </button>
                    {transcriptionErrors[index] && (
                      <p className="text-xs text-red-600">{transcriptionErrors[index]}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Per-file media links */}
            {linkableTypes.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => toggleLinks(index)}
                  className="flex w-full items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span>Link to media</span>
                  <div className="flex items-center gap-2">
                    {Object.values(fileData.links).flat().length > 0 && (
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                        {Object.values(fileData.links).flat().length}
                      </span>
                    )}
                    <FiChevronRight
                      className={`h-4 w-4 text-gray-400 transition-transform ${expandedLinks[index] ? "rotate-90" : ""}`}
                    />
                  </div>
                </button>

                {expandedLinks[index] && (
                  <div className="mt-2 space-y-2">
                    {linkableTypes.map((linkType) => (
                      <MediaLinkSection
                        key={linkType}
                        mediaType={linkType}
                        sourceMediaId={null}
                        currentLinks={fileData.links[linkType] || []}
                        onLink={(ids) => handleLinkChange(index, linkType, ids)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="border-t pt-4 text-sm text-gray-500">
        {files.length} {files.length === 1 ? "file" : "files"} selected
      </div>
    </div>
  );
}

export default UploadPreviewPanel;
